import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { logEvent } from '~/config/firebase.config';
import type { SubscriptionStatus } from '~/services/subscription/getSubscriptionStatus';
import { keepSubscription } from '~/services/subscription/subscriptionActions';
import { monthlyPrice, unixDate } from '~/utils/pauseFormat';
import { annualPriceLabel, formatAmount } from '~/utils/trialPricing';
import { keepEndAt, keepFailure } from '~/utils/keepOffer';

import styles from './Account.redesign.module.css';

/**
 * Bloc « Garder Unboared » (page Mon compte) : un essai ou un abonnement dont
 * l'arrêt est programmé continue en un clic, sans passer par le portail Stripe
 * (avant : Gérer → portail → « Renouveler »). Rien n'est prélevé au clic : la
 * prochaine échéance reste la même. `fromEmail` : arrivée par le lien de
 * l'email de fin d'essai (`/account/keep`) → bloc mis en avant + mesure.
 * Rendu seulement pour un admin (le serveur le vérifie aussi).
 */
export function KeepSubscriptionCard({
    clientID,
    sub,
    fromEmail,
    onKept,
}: {
    clientID: string;
    sub: SubscriptionStatus;
    fromEmail: boolean;
    /** Reçoit le message de confirmation (le bloc disparaît à la relecture du statut). */
    onKept: (notice: string) => void;
}) {
    const { t, i18n } = useTranslation();
    const [keeping, setKeeping] = useState(false);
    // Clé du message d'erreur affiché (null = pas d'erreur).
    const [failed, setFailed] = useState<string | null>(null);
    const ref = useRef<HTMLDivElement>(null);
    const origin = fromEmail ? 'email' : 'account';

    useEffect(() => {
        logEvent('account_keep_view', 'Account', { clientID, origin, status: sub.status });
        if (fromEmail) ref.current?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
        // Une mesure par affichage du bloc (pas à chaque relecture du statut).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const lang = i18n.language;
    const trialing = sub.status === 'trialing';
    const endAt = keepEndAt(sub);
    const date = endAt ? unixDate(endAt, lang) : '';
    // 1er prélèvement : fin d'essai (même si l'accès devait s'arrêter avant), sinon fin de période.
    const chargeAt = (trialing ? sub.trialEnd : sub.currentPeriodEnd) || endAt;
    const chargeDate = chargeAt ? unixDate(chargeAt, lang) : date;
    const yearly = sub.interval === 'year';
    // Remise (code partenaire) sans montant réel connu : jamais le prix catalogue, qui serait faux.
    const priceUnknown = !!sub.discounted && !(typeof sub.nextAmount === 'number' && sub.nextAmount > 0);
    const knownAmount = typeof sub.nextAmount === 'number' && sub.nextAmount > 0 && sub.currency;
    let price = monthlyPrice(sub, lang);
    if (yearly)
        price = knownAmount
            ? formatAmount(sub.nextAmount as number, sub.currency as string, lang)
            : annualPriceLabel(sub.currency);
    const variant = `${yearly ? 'Year' : ''}${priceUnknown ? 'NoPrice' : ''}`;

    const handleKeep = async () => {
        setKeeping(true);
        setFailed(null);
        logEvent('account_keep_click', 'Account', { clientID, origin, status: sub.status });
        try {
            await keepSubscription(clientID, origin);
            logEvent('account_keep_done', 'Account', { clientID, origin, status: sub.status });
            onKept(
                t(`account.keep.done${trialing ? 'Trial' : 'Paid'}${priceUnknown ? 'NoPrice' : ''}`, {
                    price,
                    date: chargeDate,
                })
            );
        } catch (error) {
            // Autre onglet en train de garder : on relit le statut plutôt que d'afficher une erreur.
            const failure = keepFailure(error);
            if (failure.reread) {
                onKept('');
                return;
            }
            setFailed(failure.messageKey);
            setKeeping(false);
        }
    };

    return (
        <div ref={ref} className={styles.annualOffer}>
            <p className={styles.annualTitle}>
                {t(trialing ? 'account.keep.trialTitle' : 'account.keep.paidTitle', { date })}
            </p>
            <p>{t(`account.keep.${trialing ? 'trial' : 'paid'}Body${variant}`, { price, date: chargeDate })}</p>
            <button type="button" className={styles.saveBtn} onClick={handleKeep} disabled={keeping}>
                {keeping ? t('account.keep.keeping') : t('account.keep.cta')}
            </button>
            {failed && (
                <p className={`${styles.msg} ${styles.msgError} ${styles.keepNotice}`} role="alert">
                    {t(failed)}
                </p>
            )}
        </div>
    );
}
